/**
 * Retainers tab (Invoicing → Retainers).
 *
 * Persistent schedule manager for recurring retainer invoices. The Monthly
 * Invoice Wizard reads from this list to build its "due this period" view —
 * tick-confirm + amend, but never bills silently.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  listRetainerSchedules,
  createRetainerSchedule,
  updateRetainerSchedule,
  deleteRetainerSchedule,
  toggleRetainerSchedule,
  getTasks,
  getBillingEntities,
  getClientGstins,
} from '../api';
import { showToast } from '../utils/toast';
import { formatDateDdMmmYy, formatRupeesUI } from '../utils/format';

// ─── Types ────────────────────────────────────────────────────────────────

type Frequency = 'MONTHLY' | 'QUARTERLY' | 'HALF_YEARLY' | 'YEARLY';

const FREQ_LABEL: Record<Frequency, string> = {
  MONTHLY: 'Monthly', QUARTERLY: 'Quarterly', HALF_YEARLY: 'Half-Yearly', YEARLY: 'Yearly',
};
// Times billed per year, for annualised value.
const FREQ_PER_YEAR: Record<Frequency, number> = { MONTHLY: 12, QUARTERLY: 4, HALF_YEARLY: 2, YEARLY: 1 };

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const fyLabelOf = (fy: number) => `FY ${fy}-${String((fy + 1) % 100).padStart(2, '0')}`;
const periodIso = (y: number, mZeroIdx: number) => `${y}-${String(mZeroIdx + 1).padStart(2, '0')}-01`;

// Snap any stored date to the start of the period (of the given frequency) that
// contains it — so an existing schedule's bounds map onto a select option.
function snapToPeriodStart(dateIso: string, freq: Frequency): string {
  const d = new Date(dateIso);
  const y = d.getUTCFullYear(); const m = d.getUTCMonth();
  const fy = m >= 3 ? y : y - 1;
  if (freq === 'YEARLY') return periodIso(fy, 3);
  if (freq === 'HALF_YEARLY') return m >= 3 && m <= 8 ? periodIso(fy, 3) : periodIso(fy, 9);
  if (freq === 'QUARTERLY') { const qm = m >= 3 && m <= 5 ? 3 : m >= 6 && m <= 8 ? 6 : m >= 9 && m <= 11 ? 9 : 0; return periodIso(qm === 0 ? y : y, qm); }
  return periodIso(y, m);
}

// Selectable period-start options for a frequency, spanning a few FYs around now.
// Value = ISO first-of-period date (what the schedule stores); label = period name.
function periodOptionsFor(freq: Frequency): { value: string; label: string }[] {
  const now = new Date();
  const curFy = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  const out: { value: string; label: string }[] = [];
  for (const fy of [curFy - 3, curFy - 2, curFy - 1, curFy, curFy + 1, curFy + 2]) {
    if (freq === 'MONTHLY') {
      for (let i = 0; i < 12; i++) { const m = 3 + i; const y = fy + Math.floor(m / 12); const mm = m % 12; out.push({ value: periodIso(y, mm), label: `${MONTHS_SHORT[mm]} ${y}` }); }
    } else if (freq === 'QUARTERLY') {
      out.push({ value: periodIso(fy, 3), label: `Q1 ${fyLabelOf(fy)}` }, { value: periodIso(fy, 6), label: `Q2 ${fyLabelOf(fy)}` }, { value: periodIso(fy, 9), label: `Q3 ${fyLabelOf(fy)}` }, { value: periodIso(fy + 1, 0), label: `Q4 ${fyLabelOf(fy)}` });
    } else if (freq === 'HALF_YEARLY') {
      out.push({ value: periodIso(fy, 3), label: `H1 ${fyLabelOf(fy)}` }, { value: periodIso(fy, 9), label: `H2 ${fyLabelOf(fy)}` });
    } else {
      out.push({ value: periodIso(fy, 3), label: fyLabelOf(fy) });
    }
  }
  return out;
}

interface Schedule {
  id: number;
  taskId: number;
  frequency: Frequency;
  amount: number | string;
  billingEntityId: number | null;
  clientGstinId: number | null;
  autoIncludeOpe: boolean;
  description: string | null;
  startPeriod: string | null;
  endPeriod: string | null;
  isActive: boolean;
  notes: string | null;
  task: {
    id: number;
    taskId: string;
    taskName: string;
    client: { id: number; clientCode: string; clientName: string } | null;
  };
  billingEntity: { id: number; name: string } | null;
  clientGstin: { id: number; gstin: string | null; label: string | null; isPrimary: boolean } | null;
}

interface TaskLite {
  id: number; taskId: string; taskName: string; status: string;
  client?: { id: number; clientCode: string; clientName: string } | null;
  billingEntity?: { id: number; name: string } | null;
  billedAmount?: number | null;
  category?: { hsnSacCode?: string } | null;
  dueDate?: string | null;   // task period end — retainer end period can't exceed this
}
interface BELite  { id: number; name: string }
interface GstLite { id: number; gstin: string | null; label: string | null; isPrimary: boolean }

// ─── Page ─────────────────────────────────────────────────────────────────

const Retainers: React.FC = () => {
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [tasks, setTasks] = useState<TaskLite[]>([]);
  const [billingEntities, setBillingEntities] = useState<BELite[]>([]);
  const [loading, setLoading] = useState(false);

  // Filters
  const [search, setSearch] = useState('');
  const [filterFreq, setFilterFreq] = useState<'' | Frequency>('');
  const [showInactive, setShowInactive] = useState(false);

  // Editor modal state
  const [editing, setEditing] = useState<Schedule | null>(null);
  const [creating, setCreating] = useState(false);

  // ── Initial fetch ─────────────────────────────────────────────────────
  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [s, t, be] = await Promise.all([
        listRetainerSchedules(showInactive ? {} : { isActive: 'true' }),
        getTasks(),
        getBillingEntities(),
      ]);
      setSchedules(s.data);
      setTasks((t.data as TaskLite[]).filter((x) => x.status === 'OPEN'));
      setBillingEntities(be.data);
    } catch (err: any) {
      showToast(err?.response?.data?.message ?? 'Failed to load retainers', 'error');
    } finally {
      setLoading(false);
    }
  }, [showInactive]);
  useEffect(() => { refresh(); }, [refresh]);

  // ── Derived view ──────────────────────────────────────────────────────
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return schedules.filter((s) => {
      if (filterFreq && s.frequency !== filterFreq) return false;
      if (q) {
        const hay = `${s.task.taskId} ${s.task.taskName} ${s.task.client?.clientCode ?? ''} ${s.task.client?.clientName ?? ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [schedules, search, filterFreq]);

  const totals = useMemo(() => {
    const active = visible.filter((s) => s.isActive);
    const monthly = active.filter((s) => s.frequency === 'MONTHLY').reduce((sum, s) => sum + Number(s.amount), 0);
    const quarterly = active.filter((s) => s.frequency === 'QUARTERLY').reduce((sum, s) => sum + Number(s.amount), 0);
    const annualised = active.reduce((sum, s) => sum + Number(s.amount) * (FREQ_PER_YEAR[s.frequency] ?? 0), 0);
    return { monthly, quarterly, annualised };
  }, [visible]);

  // ── Actions ────────────────────────────────────────────────────────────
  const onDelete = async (s: Schedule) => {
    if (!window.confirm(`Delete the ${s.frequency.toLowerCase()} retainer for ${s.task.taskId} – ${s.task.taskName}?\n\nThis only removes the schedule. Past invoices and the task itself are untouched.`)) return;
    try {
      await deleteRetainerSchedule(s.id);
      showToast('Retainer schedule deleted', 'success');
      refresh();
    } catch (err: any) {
      showToast(err?.response?.data?.message ?? 'Delete failed', 'error');
    }
  };

  const onToggle = async (s: Schedule) => {
    try {
      await toggleRetainerSchedule(s.id);
      showToast(s.isActive ? 'Schedule deactivated' : 'Schedule activated', 'success');
      refresh();
    } catch (err: any) {
      showToast(err?.response?.data?.message ?? 'Toggle failed', 'error');
    }
  };

  return (
    <div className="space-y-4">
      {/* Header strip */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Retainer Schedules</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Recurring invoices the Monthly Invoice Wizard suggests each period. Nothing is billed automatically — every invoice still requires tick-confirmation in the wizard.
          </p>
        </div>
        <button
          onClick={() => { setEditing(null); setCreating(true); }}
          className="btn-primary text-sm"
        >
          + New Retainer
        </button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <SummaryCard label="Monthly retainers"   value={`₹${formatRupeesUI(totals.monthly)}`}   sub="per month, active" />
        <SummaryCard label="Quarterly retainers" value={`₹${formatRupeesUI(totals.quarterly)}`} sub="per quarter, active" />
        <SummaryCard label="Annualised value"    value={`₹${formatRupeesUI(totals.annualised)}`} sub="all active, per year" highlight />
      </div>

      {/* Filter row */}
      <div className="flex flex-wrap items-center gap-3 bg-white border border-gray-200 rounded-lg px-3 py-2">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by task or client…"
          className="input-field text-sm py-1 w-64"
        />
        <select
          value={filterFreq}
          onChange={(e) => setFilterFreq(e.target.value as any)}
          className="input-field text-sm py-1 w-44"
        >
          <option value="">All frequencies</option>
          <option value="MONTHLY">📅 Monthly</option>
          <option value="QUARTERLY">🗓 Quarterly</option>
          <option value="HALF_YEARLY">🗓 Half-Yearly</option>
          <option value="YEARLY">🗓 Yearly</option>
        </select>
        <label className="flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer">
          <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
          Include inactive
        </label>
        <span className="ml-auto text-xs text-gray-500">{visible.length} schedule{visible.length === 1 ? '' : 's'}</span>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="text-center py-10 text-gray-400 text-sm">Loading…</div>
        ) : visible.length === 0 ? (
          <div className="text-center py-10 text-gray-400 text-sm">
            {schedules.length === 0
              ? 'No retainer schedules yet — click "+ New Retainer" to add your first.'
              : 'No schedules match the current filters.'}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase text-gray-500">
              <tr>
                <th className="text-left px-3 py-2">Task</th>
                <th className="text-left px-3 py-2">Client</th>
                <th className="text-left px-3 py-2">Frequency</th>
                <th className="text-right px-3 py-2">Amount</th>
                <th className="text-left px-3 py-2">Billing Entity</th>
                <th className="text-left px-3 py-2">GSTIN</th>
                <th className="text-center px-3 py-2" title="Auto-include unbilled OPE">OPE</th>
                <th className="text-left px-3 py-2">Period window</th>
                <th className="text-center px-3 py-2">Active</th>
                <th className="text-right px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((s) => (
                <tr key={s.id} className={`border-b border-gray-50 hover:bg-gray-50/60 ${!s.isActive ? 'opacity-60' : ''}`}>
                  <td className="px-3 py-2">
                    <div className="font-mono text-blue-600 font-medium">{s.task.taskId}</div>
                    <div className="text-gray-500 text-xs truncate max-w-[200px]" title={s.task.taskName}>{s.task.taskName}</div>
                  </td>
                  <td className="px-3 py-2 text-gray-700 truncate max-w-[160px]" title={s.task.client?.clientName ?? ''}>
                    {s.task.client?.clientName ?? '—'}
                  </td>
                  <td className="px-3 py-2">
                    <span className={`inline-block px-1.5 py-0.5 rounded text-[11px] border ${s.frequency === 'MONTHLY' ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-purple-50 text-purple-700 border-purple-200'}`}>
                      {FREQ_LABEL[s.frequency] ?? s.frequency}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right font-medium whitespace-nowrap">₹{formatRupeesUI(s.amount)}</td>
                  <td className="px-3 py-2 text-xs text-gray-700 truncate max-w-[140px]" title={s.billingEntity?.name ?? ''}>
                    {s.billingEntity?.name ?? <span className="text-amber-600">— not set</span>}
                  </td>
                  <td className="px-3 py-2 text-xs font-mono text-gray-700 truncate max-w-[160px]" title={s.clientGstin?.gstin ?? ''}>
                    {s.clientGstin?.gstin ?? <span className="text-amber-600 font-sans">— use primary</span>}
                  </td>
                  <td className="px-3 py-2 text-center">
                    {s.autoIncludeOpe ? <span className="text-emerald-600">✓</span> : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-500">
                    {s.startPeriod || s.endPeriod
                      ? `${s.startPeriod ? formatDateDdMmmYy(s.startPeriod) : '—'} → ${s.endPeriod ? formatDateDdMmmYy(s.endPeriod) : 'open'}`
                      : 'open-ended'}
                  </td>
                  <td className="px-3 py-2 text-center">
                    <button
                      onClick={() => onToggle(s)}
                      className={`text-xs px-2 py-0.5 rounded border ${s.isActive ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100' : 'bg-gray-100 text-gray-600 border-gray-200 hover:bg-gray-200'}`}
                      title={s.isActive ? 'Click to deactivate (wizard will skip it)' : 'Click to activate'}
                    >
                      {s.isActive ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button onClick={() => { setEditing(s); setCreating(false); }} className="text-blue-600 hover:text-blue-800 text-xs px-2">Edit</button>
                    <button onClick={() => onDelete(s)} className="text-red-600 hover:text-red-800 text-xs px-2">Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {(editing || creating) && (
        <ScheduleEditor
          schedule={editing}
          tasks={tasks}
          billingEntities={billingEntities}
          existingTaskCounts={schedules.reduce((m, s) => m.set(s.taskId, (m.get(s.taskId) ?? 0) + 1), new Map<number, number>())}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={() => { setEditing(null); setCreating(false); refresh(); }}
        />
      )}
    </div>
  );
};

// ─── Summary card ─────────────────────────────────────────────────────────

const SummaryCard: React.FC<{ label: string; value: string; sub: string; highlight?: boolean }> = ({ label, value, sub, highlight }) => (
  <div className={`rounded-xl border p-4 ${highlight ? 'bg-indigo-50 border-indigo-200' : 'bg-white border-gray-200'}`}>
    <div className="text-xs uppercase tracking-wider text-gray-500">{label}</div>
    <div className={`text-2xl font-bold mt-1 ${highlight ? 'text-indigo-700' : 'text-gray-900'}`}>{value}</div>
    <div className="text-[11px] text-gray-500 mt-0.5">{sub}</div>
  </div>
);

// ─── Editor modal ─────────────────────────────────────────────────────────

const ScheduleEditor: React.FC<{
  schedule: Schedule | null;
  tasks: TaskLite[];
  billingEntities: BELite[];
  existingTaskCounts: Map<number, number>;
  onClose: () => void;
  onSaved: () => void;
}> = ({ schedule, tasks, billingEntities, existingTaskCounts, onClose, onSaved }) => {
  const isEdit = !!schedule;

  // Form state
  const [taskId, setTaskId] = useState<number | ''>(schedule?.taskId ?? '');
  const [frequency, setFrequency] = useState<Frequency>(schedule?.frequency ?? 'MONTHLY');
  const [amount, setAmount] = useState<string>(schedule ? String(schedule.amount) : '');
  const [billingEntityId, setBillingEntityId] = useState<number | ''>(schedule?.billingEntityId ?? '');
  const [clientGstinId, setClientGstinId] = useState<number | ''>(schedule?.clientGstinId ?? '');
  const [autoIncludeOpe, setAutoIncludeOpe] = useState<boolean>(schedule?.autoIncludeOpe ?? true);
  const [description, setDescription] = useState<string>(schedule?.description ?? '');
  const [startPeriod, setStartPeriod] = useState<string>(schedule?.startPeriod ? snapToPeriodStart(schedule.startPeriod.slice(0, 10), schedule.frequency) : '');
  const [endPeriod, setEndPeriod] = useState<string>(schedule?.endPeriod ? snapToPeriodStart(schedule.endPeriod.slice(0, 10), schedule.frequency) : '');
  const [isActive, setIsActive] = useState<boolean>(schedule?.isActive ?? true);
  const [notes, setNotes] = useState<string>(schedule?.notes ?? '');

  // GSTINs for the selected task's client (loaded lazily)
  const [gstins, setGstins] = useState<GstLite[]>([]);
  const selectedTask = useMemo(() => tasks.find((t) => t.id === Number(taskId)) ?? null, [taskId, tasks]);

  useEffect(() => {
    if (!selectedTask?.client?.id) { setGstins([]); return; }
    getClientGstins(selectedTask.client.id)
      .then((r) => setGstins(r.data as GstLite[]))
      .catch(() => setGstins([]));
  }, [selectedTask?.client?.id]);

  // When a task is freshly picked (create flow), seed sensible defaults
  // from the task: amount = billedAmount, billing entity = task's BE if set.
  useEffect(() => {
    if (isEdit || !selectedTask) return;
    if (selectedTask.billedAmount != null && !amount) setAmount(String(selectedTask.billedAmount));
    if (selectedTask.billingEntity?.id && !billingEntityId) setBillingEntityId(selectedTask.billingEntity.id);
    // eslint-disable-next-line
  }, [taskId]);

  // A task may have more than one retainer schedule, so every task stays
  // selectable. Tasks that already have one are flagged in the picker so the
  // user adds a second deliberately rather than by accident.
  const taskOptions = useMemo(() => {
    return [...tasks]
      .sort((a, b) => (a.client?.clientName ?? '').localeCompare(b.client?.clientName ?? ''));
  }, [tasks]);

  // Searchable task picker
  const [taskQuery, setTaskQuery] = useState('');
  const [taskOpen, setTaskOpen] = useState(false);
  const filteredTaskOptions = useMemo(() => {
    const q = taskQuery.trim().toLowerCase();
    if (!q) return taskOptions;
    return taskOptions.filter((t) =>
      t.taskId.toLowerCase().includes(q) || (t.taskName || '').toLowerCase().includes(q) ||
      (t.client?.clientName || '').toLowerCase().includes(q) || (t.client?.clientCode || '').toLowerCase().includes(q));
  }, [taskOptions, taskQuery]);

  // Frequency-aware period options; end period can't exceed the task's due date.
  const periodOpts = useMemo(() => periodOptionsFor(frequency), [frequency]);
  const taskDue = selectedTask?.dueDate ? selectedTask.dueDate.slice(0, 10) : '';
  const endPeriodOpts = useMemo(() => (taskDue ? periodOpts.filter((o) => o.value <= taskDue) : periodOpts), [periodOpts, taskDue]);

  // Changing frequency invalidates any picked period bounds (different granularity).
  const changeFrequency = (f: Frequency) => {
    if (f === frequency) return;
    setFrequency(f); setStartPeriod(''); setEndPeriod('');
  };

  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!taskId) { showToast('Pick a task', 'error'); return; }
    if (!amount || Number(amount) < 0) { showToast('Amount must be 0 or more', 'error'); return; }
    if (startPeriod && endPeriod && endPeriod < startPeriod) { showToast('End period cannot be before start period', 'error'); return; }
    if (endPeriod && taskDue && endPeriod > taskDue) { showToast("End period cannot be later than the task's period", 'error'); return; }
    setSaving(true);
    const payload = {
      taskId: Number(taskId),
      frequency,
      amount: Number(amount),
      billingEntityId: billingEntityId ? Number(billingEntityId) : null,
      clientGstinId:   clientGstinId   ? Number(clientGstinId)   : null,
      autoIncludeOpe,
      description: description.trim() || null,
      startPeriod: startPeriod || null,
      endPeriod:   endPeriod || null,
      isActive,
      notes: notes.trim() || null,
    };
    try {
      if (isEdit && schedule) {
        await updateRetainerSchedule(schedule.id, payload);
        showToast('Schedule updated', 'success');
      } else {
        await createRetainerSchedule(payload);
        showToast('Schedule created', 'success');
      }
      onSaved();
    } catch (err: any) {
      showToast(err?.response?.data?.message ?? 'Save failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[92vh] overflow-y-auto">
        <div className="px-5 py-3 border-b flex items-center justify-between bg-gradient-to-r from-indigo-50 to-blue-50">
          <h3 className="text-lg font-bold text-gray-900">
            {isEdit ? 'Edit Retainer Schedule' : '+ New Retainer Schedule'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">✕</button>
        </div>

        <div className="p-5 space-y-4">
          {/* Task picker — searchable */}
          <div>
            <label className="label">Task <span className="text-red-500">*</span></label>
            {isEdit ? (
              <input className="input-field bg-gray-50" disabled
                value={selectedTask ? `${selectedTask.taskId} — ${selectedTask.taskName}${selectedTask.client ? ` · ${selectedTask.client.clientName}` : ''}` : ''} />
            ) : (
              <div className="relative">
                <input
                  className="input-field"
                  placeholder="Search by task id, name or client…"
                  value={taskOpen ? taskQuery : (selectedTask ? `${selectedTask.taskId} — ${selectedTask.taskName}${selectedTask.client ? ` · ${selectedTask.client.clientName}` : ''}` : '')}
                  onFocus={() => { setTaskOpen(true); setTaskQuery(''); }}
                  onChange={(e) => { setTaskQuery(e.target.value); setTaskOpen(true); }}
                  onBlur={() => setTimeout(() => setTaskOpen(false), 150)}
                />
                {taskOpen && (
                  <div className="absolute z-20 mt-1 w-full max-h-60 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg">
                    {filteredTaskOptions.length === 0 ? (
                      <div className="px-3 py-2 text-sm text-gray-400">No matching tasks</div>
                    ) : filteredTaskOptions.slice(0, 100).map((t) => {
                      const existing = existingTaskCounts.get(t.id) ?? 0;
                      return (
                      <button key={t.id} type="button"
                        className="w-full text-left px-3 py-1.5 text-sm hover:bg-blue-50 flex flex-col"
                        onMouseDown={(e) => { e.preventDefault(); setTaskId(t.id); setTaskOpen(false); setTaskQuery(''); }}>
                        <span>
                          <span className="font-mono text-blue-600">{t.taskId}</span> — {t.taskName}
                          {existing > 0 && <span className="ml-1 text-[10px] text-amber-600">· already has {existing} retainer{existing > 1 ? 's' : ''}</span>}
                        </span>
                        {t.client && <span className="text-[11px] text-gray-500">{t.client.clientCode} · {t.client.clientName}</span>}
                      </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
            {isEdit && <p className="text-[11px] text-gray-500 mt-1">Task can't be changed on an existing schedule — delete and re-create to move the retainer to a different task.</p>}
          </div>

          {/* Frequency */}
          <div>
            <label className="label">Frequency <span className="text-red-500">*</span></label>
            <div className="flex gap-2">
              {(['MONTHLY', 'QUARTERLY', 'HALF_YEARLY', 'YEARLY'] as const).map((f) => (
                <button
                  key={f}
                  type="button"
                  onClick={() => changeFrequency(f)}
                  className={`px-3 py-1.5 text-sm font-medium rounded border ${frequency === f ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
                >
                  {FREQ_LABEL[f]}
                </button>
              ))}
            </div>
          </div>

          {/* Amount + OPE */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Amount per period (₹) <span className="text-red-500">*</span></label>
              <input
                type="number"
                step="0.01"
                className="input-field"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="e.g. 25000"
              />
            </div>
            <div>
              <label className="label">Out-of-pocket expenses</label>
              <label className="flex items-center gap-2 mt-2 cursor-pointer text-sm">
                <input type="checkbox" checked={autoIncludeOpe} onChange={(e) => setAutoIncludeOpe(e.target.checked)} />
                Auto-include unbilled OPE on this task
              </label>
            </div>
          </div>

          {/* Billing entity + GSTIN */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Billing Entity</label>
              <select
                className="input-field"
                value={billingEntityId}
                onChange={(e) => setBillingEntityId(e.target.value ? Number(e.target.value) : '')}
              >
                <option value="">— Use task's BE / batch default —</option>
                {billingEntities.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Client GSTIN</label>
              <select
                className="input-field font-mono text-xs"
                value={clientGstinId}
                onChange={(e) => setClientGstinId(e.target.value ? Number(e.target.value) : '')}
                disabled={!selectedTask?.client}
              >
                <option value="">— Use client's primary GSTIN —</option>
                {gstins.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.gstin ?? `GSTIN #${g.id}`}{g.isPrimary ? ' ★' : ''}{g.label ? ` (${g.label})` : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Period window */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Start period (optional)</label>
              <select className="input-field" value={startPeriod} onChange={(e) => setStartPeriod(e.target.value)}>
                <option value="">— Always active —</option>
                {periodOpts.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <p className="text-[11px] text-gray-500 mt-1">Wizard only suggests this retainer from this period onward. Blank = always active.</p>
            </div>
            <div>
              <label className="label">End period (optional)</label>
              <select className="input-field" value={endPeriod} onChange={(e) => setEndPeriod(e.target.value)}>
                <option value="">— Open-ended —</option>
                {endPeriodOpts.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <p className="text-[11px] text-gray-500 mt-1">
                Blank = open-ended.{taskDue ? ' Capped at the task period.' : ''}
              </p>
            </div>
          </div>

          {/* Description template */}
          <div>
            <label className="label">Line description template (optional)</label>
            <input
              type="text"
              className="input-field"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={`e.g. "Retainer fee — {periodLabel}"`}
            />
            <p className="text-[11px] text-gray-500 mt-1">
              Falls back to <span className="font-mono">{'{taskName} — {periodLabel}'}</span> if blank. Use <span className="font-mono">{'{periodLabel}'}</span> to insert the period (e.g. "May 2026" or "Q1 FY 2026-27").
            </p>
          </div>

          {/* Active toggle */}
          <div className="flex items-center justify-between border-t pt-4">
            <label className="flex items-center gap-2 cursor-pointer text-sm">
              <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
              <span className="font-medium">Active</span>
              <span className="text-gray-500">— wizard will list this schedule each period</span>
            </label>
          </div>

          <div>
            <label className="label">Notes (internal)</label>
            <textarea
              className="input-field"
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Anything the wizard reviewer should know about this retainer"
            />
          </div>
        </div>

        <div className="px-5 py-3 border-t bg-gray-50 flex justify-end gap-2">
          <button onClick={onClose} className="btn-secondary text-sm" disabled={saving}>Cancel</button>
          <button onClick={submit} className="btn-primary text-sm" disabled={saving}>
            {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create schedule'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Retainers;
